import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Image,
  Send,
  Edit,
  Trash2,
  MoreHorizontal,
  Facebook,
  Instagram,
  Eye,
  TrendingUp,
  Upload,
  X,
  Film,
  Loader2,
  LayoutTemplate,
  Sparkles,
  Square,
  RectangleVertical,
  Video,
  Search,
  Check,
  Heart,
  MessageCircle,
  Share2,
  BarChart3,
  Bookmark,
  Users,
  RefreshCw,
  List,
  CalendarDays,
  Building2,
  CalendarRange,
  FileText
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ContentToolbar from '../components/ContentToolbar';
import SocialCalendarView from '../components/social/SocialCalendarView';
import PostPreviewDialog from '../components/social/PostPreviewDialog';
import AIPostGeneratorDialog from '../components/social/AIPostGeneratorDialog';
import AIPlanDialog from '../components/social/AIPlanDialog';
import PhotoPickerDialog from '../components/social/PhotoPickerDialog';
import ReportsDialog from '../components/social/ReportsDialog';
import { useSearchParams } from 'react-router-dom';
import TikTokIcon from '../components/icons/TikTokIcon';

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
};

const platformColors = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  tiktok: 'bg-black',
};

const statusColors = {
  draft: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  scheduled: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  published: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  failed: 'bg-red-500/10 text-red-600 border-red-500/20',
  pending_approval: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
};

const statusLabels = {
  draft: 'Bozza',
  scheduled: 'Programmato',
  published: 'Pubblicato',
  failed: 'Fallito',
  pending_approval: 'In approvazione',
};

const templateTypeIcons = {
  post: Square,
  story: RectangleVertical,
  reel: Video,
};

const templateTypeLabels = {
  post: 'Post',
  story: 'Story',
  reel: 'Reel',
};

const categoryLabels = {
  promozioni: 'Promozioni',
  festivita: 'Festività',
  stagionali: 'Stagionali',
  novita: 'Novità',
  consigli: 'Consigli & Tips',
  team: 'Team & Staff',
  prima_dopo: 'Prima & Dopo',
  recensioni: 'Recensioni',
  generale: 'Generale',
};

const SocialPage = () => {
  const { API, getAuthHeaders, user, actAs, setActAs } = useAuth();
  const [searchParams] = useSearchParams();
  const isSuperadmin = user?.role === 'superadmin';
  const [accounts, setAccounts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [photoPickerPost, setPhotoPickerPost] = useState(null);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [templateFilter, setTemplateFilter] = useState({ platform: 'all', category: 'all', type: 'all' });
  const [insightsLoading, setInsightsLoading] = useState({});
  const [postInsights, setPostInsights] = useState({});
  const [expandedInsights, setExpandedInsights] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [previewPost, setPreviewPost] = useState(null);
  const [formData, setFormData] = useState({
    content: '',
    platforms: [],
    scheduled_time: null,
    post_status: 'draft',
    media_urls: [],
    post_format: 'post'
  });

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts`, getAuthHeaders());
      setPosts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento post:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/templates`, getAuthHeaders()),
        axios.get(`${API}/templates/categories`, getAuthHeaders())
      ]);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Errore nel caricamento template:', error);
    }
  }, [API, getAuthHeaders]);

  const fetchPostInsights = async (postId) => {
    setInsightsLoading(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await axios.get(`${API}/social/insights/${postId}`, getAuthHeaders());
      setPostInsights(prev => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error('Errore nel caricamento insights:', error);
    } finally {
      setInsightsLoading(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleInsights = (postId) => {
    const isExpanded = expandedInsights[postId];
    setExpandedInsights(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded && !postInsights[postId]) {
      fetchPostInsights(postId);
    }
  };

  const fetchAllPublishedInsights = useCallback(() => {
    const publishedPosts = posts.filter(p => p.post_status === 'published' && p.publish_results);
    for (const post of publishedPosts) {
      if (post.cached_insights) {
        setPostInsights(prev => ({ ...prev, [post.id]: post.cached_insights }));
      }
    }
  }, [posts]);

  useEffect(() => {
    fetchPosts();
    fetchTemplates();
  }, [fetchPosts, fetchTemplates]);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-social/usage`, getAuthHeaders());
      setUsage(res.data);
    } catch (error) {
      console.error('Errore nel caricamento quota AI:', error);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  useEffect(() => {
    if (!isSuperadmin) return;
    const fromUrl = searchParams.get('account');
    if (fromUrl) setActAs(fromUrl);
    axios.get(`${API}/ai-social/admin/accounts`, { headers: { Authorization: getAuthHeaders().headers.Authorization } })
      .then(res => setAccounts(res.data))
      .catch(err => console.error('Errore nel caricamento account:', err));
    return () => setActAs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const handleApprove = async (postId) => {
    try {
      await axios.post(`${API}/ai-social/posts/${postId}/approve`, {}, getAuthHeaders());
      toast.success('Post approvato e programmato');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    }
  };

  const handleApproveAll = async () => {
    try {
      const res = await axios.post(`${API}/ai-social/posts/approve-all`, {}, getAuthHeaders());
      toast.success(`${res.data.approved} post approvati e programmati`);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    }
  };

  const activeAccount = accounts.find(a => a.id === actAs);

  useEffect(() => {
    if (posts.length > 0) {
      fetchAllPublishedInsights();
    }
  }, [posts, fetchAllPublishedInsights]);

  const applyTemplate = (template) => {
    // Convert template media URLs to media objects with types and thumbnails
    const mediaObjects = (template.media_urls || []).map((url, idx) => {
      const mediaType = template.media_types?.[idx] || (typeof url === 'string' && url.match(/\.(mp4|mov|webm|avi)$/i) ? 'video' : 'image');
      const thumbUrl = template.thumbnails?.[idx] || null;
      return {
        id: `template-${idx}`,
        url: url,
        filename: url.split('/').pop(),
        media_type: mediaType,
        thumbnail_url: thumbUrl
      };
    });

    setFormData({
      content: template.content,
      platforms: template.platforms,
      scheduled_time: null,
      post_status: 'draft',
      media_urls: mediaObjects
    });
    setIsTemplateDialogOpen(false);
    setIsDialogOpen(true);
  };

  const filteredTemplates = templates.filter(t => {
    if (templateFilter.platform !== 'all' && !t.platforms.includes(templateFilter.platform)) return false;
    if (templateFilter.category !== 'all' && t.category !== templateFilter.category) return false;
    if (templateFilter.type !== 'all' && t.template_type !== templateFilter.type) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert media objects to URL strings for backend
      const submitData = {
        ...formData,
        media_urls: formData.media_urls.map(m => typeof m === 'string' ? m : m.url),
        post_format: formData.post_format || 'post'
      };
      
      if (selectedPost) {
        await axios.put(`${API}/posts/${selectedPost.id}`, submitData, getAuthHeaders());
      } else {
        await axios.post(`${API}/posts`, submitData, getAuthHeaders());
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio post:', error);
    }
  };

  const handleDelete = async (id) => {
    const post = posts.find(p => p.id === id);
    const hasSocialPosts = post?.publish_results && (
      post.publish_results.facebook?.success || post.publish_results.instagram?.success
    );
    
    const confirmMsg = hasSocialPosts 
      ? 'Sei sicuro? Il post verrà eliminato dall\'app e da Facebook (Instagram non supporta l\'eliminazione via API).'
      : 'Sei sicuro di voler eliminare questo post?';
    
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.delete(`${API}/posts/${id}`, getAuthHeaders());
      const platformDeletions = response.data.platform_deletions || {};
      
      let feedback = 'Post eliminato dall\'app.';
      if (platformDeletions.facebook) {
        feedback += platformDeletions.facebook.deleted 
          ? '\nFacebook: eliminato con successo.' 
          : `\nFacebook: ${platformDeletions.facebook.error || 'eliminazione non riuscita.'}`;
      }
      if (platformDeletions.instagram) {
        feedback += platformDeletions.instagram.deleted 
          ? '\nInstagram: eliminato con successo.' 
          : `\nInstagram: ${platformDeletions.instagram.error || 'eliminazione non riuscita.'}`;
      }
      
      if (Object.keys(platformDeletions).length > 0) {
        toast.success(feedback, { duration: 6000 });
      } else {
        toast.success(feedback);
      }
      
      fetchPosts();
    } catch (error) {
      console.error('Errore nell\'eliminazione post:', error);
      toast.error('Errore nell\'eliminazione del post.');
    }
  };

  const [publishingId, setPublishingId] = useState(null);

  const handlePublish = async (id) => {
    setPublishingId(id);
    try {
      const publishResult = await axios.post(`${API}/social/publish/${id}`, {}, getAuthHeaders());
      
      const results = publishResult.data.results;
      const lines = [];
      let hasSuccess = false;
      let hasError = false;
      
      if (results.facebook) {
        if (results.facebook.success) {
          lines.push('Facebook: Pubblicato con successo');
          hasSuccess = true;
        } else {
          lines.push(`Facebook: ${results.facebook.error}`);
          hasError = true;
        }
      }
      if (results.instagram) {
        if (results.instagram.success) {
          lines.push('Instagram: Pubblicato con successo');
          hasSuccess = true;
        } else {
          let igError = results.instagram.error || 'Errore sconosciuto';
          if (igError.toLowerCase().includes('aspect ratio')) {
            igError += ' (aspect ratio tra 4:5 e 1.91:1 richiesto)';
          }
          lines.push(`Instagram: ${igError}`);
          hasError = true;
        }
      }
      
      if (!results.facebook && !results.instagram) {
        toast.info('Post salvato come pubblicato. Collega i tuoi account social nelle Impostazioni per pubblicare direttamente.', { duration: 6000 });
      } else if (hasSuccess && !hasError) {
        toast.success('Pubblicazione completata!', { description: lines.join(' • '), duration: 5000 });
      } else if (hasError && !hasSuccess) {
        toast.error('Pubblicazione fallita', { description: lines.join(' • '), duration: 8000 });
      } else {
        toast.warning('Pubblicazione parziale', { description: lines.join(' • '), duration: 8000 });
      }
      
      fetchPosts();
    } catch (error) {
      console.error('Errore nella pubblicazione post:', error);
      toast.error('Errore nella pubblicazione. Riprova.');
    } finally {
      setPublishingId(null);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newMediaUrls = [...formData.media_urls];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/upload/media`,
          formDataUpload,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        newMediaUrls.push({
          id: response.data.id,
          url: response.data.url,
          filename: response.data.filename,
          media_type: response.data.media_type,
          thumbnail_url: response.data.thumbnail_url || null
        });
      } catch (error) {
        const detail = error.response?.data?.detail || error.message || 'Errore sconosciuto';
        toast.error(`Errore nel caricamento di ${file.name}: ${detail}`);
      }
    }
    
    setFormData(prev => ({ ...prev, media_urls: newMediaUrls }));
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const removeMedia = async (index) => {
    const media = formData.media_urls[index];
    try {
      if (media.id) {
        await axios.delete(`${API}/upload/${media.id}`, getAuthHeaders());
      }
    } catch (error) {
      console.error('Errore eliminazione file:', error);
    }
    
    setFormData(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({ content: '', platforms: [], scheduled_time: null, post_status: 'draft', media_urls: [], post_format: 'post' });
    setSelectedPost(null);
    setIsDialogOpen(false);
  };

  const openEdit = (post) => {
    setSelectedPost(post);
    // Convert URL strings to media objects for display
    const mediaObjects = (post.media_urls || []).map((url, idx) => {
      if (typeof url === 'string') {
        const isVideo = url.match(/\.(mp4|mov|webm|avi)$/i);
        return {
          id: `existing-${idx}`,
          url: url,
          filename: url.split('/').pop(),
          media_type: isVideo ? 'video' : 'image'
        };
      }
      return url;
    });
    
    setFormData({
      content: post.content,
      platforms: post.platforms,
      scheduled_time: post.scheduled_time,
      post_status: post.post_status,
      media_urls: mediaObjects,
      post_format: post.post_format || 'post'
    });
    setIsDialogOpen(true);
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleReschedule = async (postId, newScheduledTime) => {
    try {
      await axios.patch(`${API}/posts/${postId}/reschedule`, { scheduled_time: newScheduledTime }, getAuthHeaders());
      fetchPosts();
    } catch (error) {
      console.error('Errore nella riprogrammazione:', error);
      toast.error(error.response?.data?.detail || 'Errore nella riprogrammazione del post');
    }
  };

  const filteredPosts = activeTab === 'all' 
    ? posts 
    : posts.filter(p => p.post_status === activeTab);

  const stats = {
    total: posts.length,
    draft: posts.filter(p => p.post_status === 'draft').length,
    scheduled: posts.filter(p => p.post_status === 'scheduled').length,
    published: posts.filter(p => p.post_status === 'published').length,
    pending: posts.filter(p => p.post_status === 'pending_approval').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="social-page">
      {/* Superadmin: account selector */}
      {isSuperadmin && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="agency-account-bar">
          <div className="flex items-center gap-2 text-sm font-medium"><Building2 className="w-4 h-4 text-primary" /> Gestisci il calendario di:</div>
          <Select value={actAs || 'self'} onValueChange={v => setActAs(v === 'self' ? null : v)}>
            <SelectTrigger className="sm:w-72 bg-background" data-testid="agency-account-select"><SelectValue placeholder="Il mio account" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="self">Il mio account (superadmin)</SelectItem>
              {accounts.map(a => (
                <SelectItem key={a.id} value={a.id} data-testid={`agency-account-option-${a.id}`}>{a.brand}{a.city ? ` · ${a.city}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeAccount && (
            <span className="text-xs text-muted-foreground">
              Approvazione: {activeAccount.settings.approval_mode === 'direct' ? 'diretta' : 'richiesta al negozio'} · Quota AI {activeAccount.usage.used}/{activeAccount.usage.limit}
            </span>
          )}
        </div>
      )}

      {/* Pending approval banner */}
      {stats.pending > 0 && !actAs && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="pending-approval-banner">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Clock className="w-4 h-4" />
            <span><strong>{stats.pending}</strong> post preparati dal tuo social media manager sono in attesa di approvazione.</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" onClick={() => setActiveTab('pending_approval')} data-testid="pending-view-btn">Rivedi</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={handleApproveAll} data-testid="pending-approve-all-btn"><Check className="w-3.5 h-3.5 mr-1" /> Approva tutti</Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Social Media</h1>
          <p className="text-muted-foreground mt-1">
            {activeAccount ? <>Stai lavorando sul calendario di <strong>{activeAccount.brand}</strong></> : 'Crea e programma post su diverse piattaforme'}
            {usage && !usage.acting_as && <span className="ml-2 text-xs" data-testid="ai-usage-chip">· Post AI: {usage.used}/{usage.limit} questo mese</span>}
          </p>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-3 px-3 pb-1 sm:mx-0 sm:px-0 sm:pb-0 sm:flex-wrap" data-testid="social-actions-bar">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg p-0.5 shrink-0" data-testid="view-mode-toggle">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3 gap-1.5"
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Lista</span>
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3 gap-1.5"
              onClick={() => setViewMode('calendar')}
              data-testid="view-calendar-btn"
            >
              <CalendarDays className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Calendario</span>
            </Button>
          </div>

          {/* Genera con AI Button */}
          <Button variant="outline" className="shrink-0 border-primary/40 text-primary hover:bg-primary/5" onClick={() => setIsAIDialogOpen(true)} data-testid="ai-generate-open-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Genera con AI
          </Button>
          <Button variant="outline" className="shrink-0 border-primary/40 text-primary hover:bg-primary/5" onClick={() => setIsPlanDialogOpen(true)} data-testid="ai-plan-open-btn">
            <CalendarRange className="w-4 h-4 mr-2" />
            Piano AI
          </Button>
          <Button variant="ghost" size="icon" className="shrink-0" title="Report mensili" onClick={() => setIsReportsOpen(true)} data-testid="reports-open-btn">
            <FileText className="w-4 h-4" />
          </Button>

          {/* Usa Modello Button */}
          <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="shrink-0" data-testid="use-template-btn">
                <LayoutTemplate className="w-4 h-4 mr-2" />
                Usa Modello
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-4xl max-h-[85vh]">
              <DialogHeader>
                <DialogTitle className="font-outfit flex items-center gap-2">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  Galleria Modelli
                </DialogTitle>
              </DialogHeader>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 py-3 border-b">
                <Select value={templateFilter.platform} onValueChange={(v) => setTemplateFilter({...templateFilter, platform: v})}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Piattaforma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={templateFilter.type} onValueChange={(v) => setTemplateFilter({...templateFilter, type: v})}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="post">Post</SelectItem>
                    <SelectItem value="story">Story</SelectItem>
                    <SelectItem value="reel">Reel</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={templateFilter.category} onValueChange={(v) => setTemplateFilter({...templateFilter, category: v})}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Badge variant="secondary" className="ml-auto">
                  {filteredTemplates.length} modelli
                </Badge>
              </div>
              
              {/* Template Gallery */}
              <ScrollArea className="h-[55vh] pr-4">
                {filteredTemplates.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
                    {filteredTemplates.map((template) => {
                      const TypeIcon = templateTypeIcons[template.template_type];
                      return (
                        <div 
                          key={template.id}
                          className="group rounded-lg border overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                          onClick={() => applyTemplate(template)}
                          data-testid={`template-card-${template.id}`}
                        >
                          {/* Media Preview */}
                          {template.media_urls && template.media_urls.length > 0 ? (
                            <div className="aspect-video bg-muted relative">
                              {(() => {
                                const mediaUrl = template.media_urls[0];
                                const isVideo = template.media_types?.[0] === 'video'
                                  || (typeof mediaUrl === 'string' && mediaUrl.match(/\.(mp4|mov|webm|avi)$/i));
                                const thumbUrl = template.thumbnails?.[0];
                                if (isVideo && thumbUrl) {
                                  return (
                                    <>
                                      <img src={`${API.replace('/api', '')}${thumbUrl}`} alt="" className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                                        <Film className="w-10 h-10 text-white drop-shadow" />
                                      </div>
                                    </>
                                  );
                                }
                                return isVideo ? (
                                  <div className="w-full h-full flex items-center justify-center bg-black/10">
                                    <Film className="w-10 h-10 text-muted-foreground" />
                                  </div>
                                ) : (
                                  <img
                                    src={`${API.replace('/api', '')}${mediaUrl}`}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                );
                              })()}
                              {/* Overlay on hover */}
                              <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="text-white text-center">
                                  <Check className="w-8 h-8 mx-auto mb-1" />
                                  <span className="text-sm font-medium">Usa questo</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="aspect-video bg-muted flex items-center justify-center relative">
                              <Image className="w-10 h-10 text-muted-foreground/30" />
                              <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="text-white text-center">
                                  <Check className="w-8 h-8 mx-auto mb-1" />
                                  <span className="text-sm font-medium">Usa questo</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Content */}
                          <div className="p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm line-clamp-1">{template.title}</h4>
                              <Badge variant="outline" className="text-xs flex items-center gap-1">
                                <TypeIcon className="w-3 h-3" />
                                {templateTypeLabels[template.template_type]}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{template.content}</p>
                            <div className="flex items-center gap-1">
                              {template.platforms.map((platform) => {
                                const Icon = platformIcons[platform];
                                return (
                                  <div 
                                    key={platform}
                                    className={`w-5 h-5 rounded ${platformColors[platform]} flex items-center justify-center`}
                                  >
                                    <Icon className="w-3 h-3 text-white" />
                                  </div>
                                );
                              })}
                              <button
                                onClick={(e) => { e.stopPropagation(); setPreviewPost({ content: template.content, platforms: template.platforms, media_urls: template.media_urls || [], post_format: template.template_type || 'post', media_types: [] }); }}
                                className="ml-auto p-1 rounded hover:bg-muted transition-colors"
                                data-testid={`preview-template-${template.id}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                              <Badge variant="secondary" className="text-xs">
                                {categoryLabels[template.category] || template.category}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <LayoutTemplate className="w-12 h-12 text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Nessun modello trovato</p>
                    <p className="text-sm text-muted-foreground mt-1">Prova a cambiare i filtri</p>
                  </div>
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {/* Crea Post Button */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="shrink-0" data-testid="create-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                Crea Post
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {selectedPost ? 'Modifica Post' : 'Crea Nuovo Post'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Piattaforme</Label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {['facebook', 'instagram', 'tiktok'].map((platform) => {
                    const Icon = platformIcons[platform];
                    const isSelected = formData.platforms.includes(platform);
                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`platform-${platform}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm capitalize">{platform}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Contenuto</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Cosa vuoi condividere?"
                  rows={5}
                  required
                  data-testid="post-content-input"
                />
                <ContentToolbar
                  value={formData.content}
                  onChange={(val) => setFormData(prev => ({...prev, content: val}))}
                  platform={formData.platforms?.[0] || 'instagram'}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.content.length} / 280 caratteri
                </p>
              </div>
              <div className="space-y-2">
                <Label>Formato</Label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {[
                    { value: 'post', label: 'Post', icon: Square },
                    { value: 'reel', label: 'Reel', icon: Video },
                    { value: 'story', label: 'Story', icon: Clock }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, post_format: value }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        formData.post_format === value 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`format-${value}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Programmazione (Opzionale)</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start" data-testid="schedule-trigger">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {formData.scheduled_time 
                          ? format(new Date(formData.scheduled_time), 'PPP', { locale: it })
                          : 'Seleziona data'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.scheduled_time ? new Date(formData.scheduled_time) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const prev = formData.scheduled_time ? new Date(formData.scheduled_time) : new Date();
                            const newDate = new Date(date);
                            newDate.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
                            setFormData(prev => ({...prev, scheduled_time: newDate.toISOString(), post_status: 'scheduled'}));
                          }
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-1">
                    <Input
                      type="time"
                      value={formData.scheduled_time 
                        ? format(new Date(formData.scheduled_time), 'HH:mm')
                        : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const [hours, minutes] = e.target.value.split(':').map(Number);
                          const base = formData.scheduled_time ? new Date(formData.scheduled_time) : new Date();
                          base.setHours(hours, minutes, 0, 0);
                          setFormData(prev => ({...prev, scheduled_time: base.toISOString(), post_status: 'scheduled'}));
                        }
                      }}
                      className="w-[110px]"
                      data-testid="schedule-time-input"
                    />
                  </div>
                </div>
                {formData.scheduled_time && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Programmato: {format(new Date(formData.scheduled_time), "d MMM yyyy 'alle' HH:mm", { locale: it })}
                    </p>
                    <button 
                      type="button"
                      onClick={() => setFormData(prev => ({...prev, scheduled_time: null, post_status: 'draft'}))}
                      className="text-xs text-destructive hover:underline"
                    >
                      Rimuovi
                    </button>
                  </div>
                )}
              </div>

              {/* Media Upload Section */}
              <div className="space-y-3">
                <Label>Media (Immagini / Video)</Label>
                
                {/* Upload Button */}
                <div className="flex flex-wrap items-center gap-3">
                  <label 
                    htmlFor="media-upload"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed cursor-pointer transition-all hover:border-primary hover:bg-primary/5 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span className="text-sm">{uploading ? 'Caricamento...' : 'Carica file'}</span>
                  </label>
                  <input
                    id="media-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                    multiple
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    data-testid="media-upload-input"
                  />
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG, GIF, WebP, MP4, MOV (max 50MB)
                  </span>
                </div>

                {/* Media Preview */}
                {formData.media_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {formData.media_urls.map((media, index) => (
                      <div key={media.url || `media-${index}`} className="relative group rounded-lg overflow-hidden border bg-muted aspect-square">
                        {media.media_type === 'video' ? (
                          media.thumbnail_url ? (
                            <>
                              <img src={`${API.replace('/api', '')}${media.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <Film className="w-6 h-6 text-white drop-shadow" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-black/10">
                              <Film className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )
                        ) : (
                          <img
                            src={`${API.replace('/api', '')}${media.url}`}
                            alt={media.filename}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-media-${index}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="absolute bottom-1 right-1 text-xs text-white bg-black/60 px-1 rounded truncate max-w-[80%]">
                          {media.filename}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => setPreviewPost({ ...formData, media_types: formData.media_urls.map(m => typeof m === 'string' ? 'image' : m.media_type || 'image') })}
                  disabled={!formData.content && formData.media_urls.length === 0}
                  data-testid="preview-form-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />Anteprima
                </Button>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                  <Button type="submit" data-testid="save-post-btn">
                    {selectedPost ? 'Aggiorna' : 'Crea'} Post
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
        {/* Statistiche */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="card-hover" onClick={() => setActiveTab('all')}>
          <CardContent className="p-3 sm:p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold font-outfit">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Post Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('draft')}>
          <CardContent className="p-3 sm:p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-500/10 flex items-center justify-center shrink-0">
                <Edit className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold font-outfit">{stats.draft}</p>
                <p className="text-xs text-muted-foreground">Bozze</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('scheduled')}>
          <CardContent className="p-3 sm:p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold font-outfit">{stats.scheduled}</p>
                <p className="text-xs text-muted-foreground">Programmati</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('published')}>
          <CardContent className="p-3 sm:p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Send className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold font-outfit">{stats.published}</p>
                <p className="text-xs text-muted-foreground">Pubblicati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista Post */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="font-outfit">
              {activeTab === 'all' ? 'Tutti i Post' : `Post ${statusLabels[activeTab]}`}
            </CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
              <TabsList className="w-full sm:w-auto flex justify-start gap-1 h-auto p-1 sm:inline-flex">
                <TabsTrigger value="all" data-testid="tab-all" className="text-xs sm:text-sm py-1.5 px-2.5">Tutti</TabsTrigger>
                <TabsTrigger value="draft" data-testid="tab-draft" className="text-xs sm:text-sm py-1.5 px-2.5">Bozze</TabsTrigger>
                <TabsTrigger value="pending_approval" data-testid="tab-pending" className="text-xs sm:text-sm py-1.5 px-2.5">Da approvare{stats.pending > 0 ? ` (${stats.pending})` : ''}</TabsTrigger>
                <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="text-xs sm:text-sm py-1.5 px-2.5">Programmati</TabsTrigger>
                <TabsTrigger value="published" data-testid="tab-published" className="text-xs sm:text-sm py-1.5 px-2.5">Pubblicati</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPosts.length > 0 ? (
            <div className="space-y-4">
              {filteredPosts.map((post, index) => (
                <div 
                  key={post.id}
                  className="p-4 rounded-lg border border-border hover:border-primary/30 transition-all animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`post-${post.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {post.platforms.map((platform) => {
                          const Icon = platformIcons[platform];
                          return (
                            <div 
                              key={platform}
                              className={`w-6 h-6 rounded-md ${platformColors[platform]} flex items-center justify-center`}
                            >
                              <Icon className="w-3.5 h-3.5 text-white" />
                            </div>
                          );
                        })}
                        <Badge className={statusColors[post.post_status]} variant="outline">
                          {statusLabels[post.post_status]}
                        </Badge>
                        {post.post_format && post.post_format !== 'post' && (
                          <Badge variant="outline" className="text-xs">
                            {post.post_format === 'reel' ? 'Reel' : post.post_format === 'story' ? 'Story' : ''}
                          </Badge>
                        )}
                        {post.ai_generated && (
                          <Badge variant="outline" className="text-xs gap-1 text-primary border-primary/40" data-testid={`post-ai-badge-${post.id}`}>
                            <Sparkles className="w-3 h-3" /> AI{post.ai_review_score != null ? ` ${post.ai_review_score}` : ''}
                          </Badge>
                        )}
                        {post.needs_media && post.post_status !== 'published' && (
                          <Badge variant="outline" className="text-xs gap-1 text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-50" onClick={() => setPhotoPickerPost(post)} data-testid={`post-needs-media-${post.id}`}>
                            <Image className="w-3 h-3" /> Aggiungi foto
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm line-clamp-2">{post.content}</p>
                      
                      {/* Media Preview in Post List */}
                      {post.media_urls && post.media_urls.length > 0 && (
                        <div className="flex gap-2 mt-2 overflow-x-auto">
                          {post.media_urls.slice(0, 4).map((media, idx) => {
                            const mediaUrl = typeof media === 'string' ? media : media.url;
                            const isVideo = post.media_types?.[idx] === 'video'
                              || (typeof media !== 'string' && media.media_type === 'video')
                              || (typeof mediaUrl === 'string' && mediaUrl.match(/\.(mp4|mov|webm|avi)$/i));
                            const thumbUrl = post.thumbnails?.[idx];
                            
                            return (
                              <div key={mediaUrl || `media-${idx}`} className="w-16 h-16 rounded-md overflow-hidden border flex-shrink-0 bg-muted relative">
                                {isVideo ? (
                                  thumbUrl ? (
                                    <>
                                      <img src={`${API.replace('/api', '')}${thumbUrl}`} alt="" className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                        <Film className="w-4 h-4 text-white drop-shadow" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-black/10">
                                      <Film className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                  )
                                ) : (
                                  <img
                                    src={`${API.replace('/api', '')}${mediaUrl}`}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                            );
                          })}
                          {post.media_urls.length > 4 && (
                            <div className="w-16 h-16 rounded-md border flex-shrink-0 flex items-center justify-center bg-muted">
                              <span className="text-xs text-muted-foreground">+{post.media_urls.length - 4}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Creato: {format(parseISO(post.created_at), 'd MMM yyyy', { locale: it })}</span>
                        {post.scheduled_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Programmato: {format(parseISO(post.scheduled_time), 'd MMM, HH:mm', { locale: it })}
                          </span>
                        )}
                      </div>

                      {/* Insights Section for Published Posts */}
                      {post.post_status === 'published' && (
                        <div className="mt-3">
                          <button
                            onClick={() => toggleInsights(post.id)}
                            className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                            data-testid={`toggle-insights-${post.id}`}
                          >
                            <BarChart3 className="w-3.5 h-3.5" />
                            {expandedInsights[post.id] ? 'Nascondi Insights' : 'Mostra Insights'}
                            {insightsLoading[post.id] && <Loader2 className="w-3 h-3 animate-spin" />}
                          </button>
                          
                          {expandedInsights[post.id] && postInsights[post.id] && (
                            <div className="mt-2 space-y-2 animate-fade-in">
                              {/* Facebook Insights */}
                              {postInsights[post.id]?.facebook && !postInsights[post.id]?.facebook?.error && (
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                      <Facebook className="w-3.5 h-3.5 text-blue-600" />
                                      <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">Facebook</span>
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); fetchPostInsights(post.id); }}
                                      className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
                                      data-testid={`refresh-fb-insights-${post.id}`}
                                    >
                                      <RefreshCw className={`w-3 h-3 text-blue-500 ${insightsLoading[post.id] ? 'animate-spin' : ''}`} />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Heart className="w-3 h-3 text-red-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].facebook.likes ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Like</p>
                                    </div>
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <MessageCircle className="w-3 h-3 text-blue-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].facebook.comments ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Commenti</p>
                                    </div>
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Share2 className="w-3 h-3 text-green-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].facebook.shares ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Condivisioni</p>
                                    </div>
                                    <div className="text-center hidden sm:block">
                                      <div className="flex items-center justify-center gap-1">
                                        <Eye className="w-3 h-3 text-purple-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].facebook.impressions ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Impression</p>
                                    </div>
                                    <div className="text-center hidden sm:block">
                                      <div className="flex items-center justify-center gap-1">
                                        <TrendingUp className="w-3 h-3 text-orange-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].facebook.engagement_rate ?? 0}%</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Engagement</p>
                                    </div>
                                  </div>
                                  {/* Permission warnings */}
                                  {postInsights[post.id].facebook.permission_warnings?.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                                      {postInsights[post.id].facebook.permission_warnings.map((w, i) => (
                                        <p key={`warning-${w.slice(0,20)}`} className="text-[10px] text-amber-600 dark:text-amber-400">{w}</p>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Facebook Error */}
                              {postInsights[post.id]?.facebook?.error && (
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <Facebook className="w-3.5 h-3.5 text-blue-600" />
                                    <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">Facebook</span>
                                  </div>
                                  <p className="text-xs text-red-600 dark:text-red-400">{postInsights[post.id].facebook.error}</p>
                                </div>
                              )}

                              {/* Instagram Insights */}
                              {postInsights[post.id]?.instagram && !postInsights[post.id]?.instagram?.error && (
                                <div className="p-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                      <Instagram className="w-3.5 h-3.5 text-pink-600" />
                                      <span className="text-xs font-semibold text-purple-700 dark:text-purple-400">Instagram</span>
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); fetchPostInsights(post.id); }}
                                      className="p-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors"
                                      data-testid={`refresh-ig-insights-${post.id}`}
                                    >
                                      <RefreshCw className={`w-3 h-3 text-purple-500 ${insightsLoading[post.id] ? 'animate-spin' : ''}`} />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Heart className="w-3 h-3 text-red-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].instagram.likes ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Like</p>
                                    </div>
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <MessageCircle className="w-3 h-3 text-blue-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].instagram.comments ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Commenti</p>
                                    </div>
                                    <div className="text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Bookmark className="w-3 h-3 text-amber-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].instagram.saved ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Salvati</p>
                                    </div>
                                    <div className="text-center hidden sm:block">
                                      <div className="flex items-center justify-center gap-1">
                                        <Users className="w-3 h-3 text-purple-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].instagram.reach ?? 0}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Copertura</p>
                                    </div>
                                    <div className="text-center hidden sm:block">
                                      <div className="flex items-center justify-center gap-1">
                                        <TrendingUp className="w-3 h-3 text-orange-500" />
                                        <span className="text-sm font-bold">{postInsights[post.id].instagram.engagement_rate ?? 0}%</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Engagement</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Instagram Error */}
                              {postInsights[post.id]?.instagram?.error && (
                                <div className="p-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <Instagram className="w-3.5 h-3.5 text-pink-600" />
                                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-400">Instagram</span>
                                  </div>
                                  <p className="text-xs text-red-600 dark:text-red-400">{postInsights[post.id].instagram.error}</p>
                                </div>
                              )}

                              {/* Show message when no insights yet */}
                              {postInsights[post.id] && !postInsights[post.id].facebook && !postInsights[post.id].instagram && (
                                <p className="text-xs text-muted-foreground italic py-2">Nessun insight disponibile. Verifica che il post sia stato pubblicato tramite una connessione social attiva nelle Impostazioni.</p>
                              )}
                              {!postInsights[post.id] && !insightsLoading[post.id] && (
                                <p className="text-xs text-muted-foreground italic py-2">Nessun insight disponibile. Collega i tuoi account social nelle Impostazioni per vedere le metriche.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {post.post_status === 'pending_approval' && (
                        <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => handleApprove(post.id)} data-testid={`approve-${post.id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" />Approva
                        </Button>
                      )}
                      {(post.post_status === 'draft' || post.post_status === 'failed') && (
                        <Button 
                          size="sm" 
                          onClick={() => handlePublish(post.id)}
                          disabled={publishingId === post.id}
                          data-testid={`publish-${post.id}`}
                        >
                          {publishingId === post.id ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Pubblicazione...</>
                          ) : (
                            <><Send className="w-3.5 h-3.5 mr-1" />Pubblica</>
                          )}
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setPreviewPost(post)} data-testid={`preview-${post.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            Anteprima
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(post)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(post.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold font-outfit mb-1">Nessun post trovato</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {activeTab === 'all' ? 'Crea il tuo primo post sui social media' : `Nessun post ${statusLabels[activeTab].toLowerCase()}`}
              </p>
              {activeTab === 'all' && (
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Post
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
        </>
      ) : (
        <SocialCalendarView
          posts={posts}
          onEdit={openEdit}
          onPublish={handlePublish}
          onReschedule={handleReschedule}
          API={API}
        />
      )}

      {/* Post Preview Dialog */}
      <AIPostGeneratorDialog open={isAIDialogOpen} onOpenChange={setIsAIDialogOpen} onSaved={() => { fetchPosts(); fetchUsage(); }} />
      <AIPlanDialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen} onSaved={() => { fetchPosts(); fetchUsage(); }} usage={usage} />
      <ReportsDialog open={isReportsOpen} onOpenChange={setIsReportsOpen} />
      <PhotoPickerDialog open={!!photoPickerPost} onOpenChange={(o) => { if (!o) setPhotoPickerPost(null); }} post={photoPickerPost} onSaved={fetchPosts} />
      <PostPreviewDialog
        open={!!previewPost}
        onClose={() => setPreviewPost(null)}
        post={previewPost}
        apiBase={API.replace('/api', '')}
      />
    </div>
  );
};

export default SocialPage;
